import { ShieldCheck, Sparkles, Heart, Instagram } from "lucide-react";
import { TEAM } from "@/data/site";
import { IMAGES, BUSINESS } from "@/config";
import SEO from "@/components/SEO";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { CTABanner } from "@/components/sections";

const HYGIENE = [
  { icon: ShieldCheck, t: "Sterilized & single-use", d: "Autoclave-sterilized metal tools and single-use files, buffers, and wax applicators for every guest." },
  { icon: Sparkles, t: "Air-purified rooms", d: "Medical-grade air purification and hospital-grade surface disinfection between every appointment." },
  { icon: Heart, t: "Clean formulas", d: "10-free polishes, ammonia-optional color, and barrier-first skincare — safer for you and our team." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About & Our Team — Luxury Salon in Hell's Kitchen, NYC"
        description="Meet the colorists, estheticians and nail artists behind Maison Neuf, a full-service luxury salon in Hell's Kitchen, Manhattan. Our story, hygiene standards and NYC community."
      />
      <section className="mx-auto max-w-[1400px] px-5 pt-36 pb-16 md:px-10 md:pt-44" data-testid="about-hero">
        <p className="label mb-6 text-wine">Our Story</p>
        <h1 className="max-w-5xl font-display text-5xl leading-[1.0] md:text-8xl">
          <MaskedLines lines={["Built by New Yorkers,", "for New Yorkers."]} />
        </h1>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-24 md:grid-cols-12 md:px-10" data-testid="about-story">
        <div className="md:col-span-5">
          <Reveal>
            <div className="clip-frame aspect-[4/5] overflow-hidden">
              <img src={IMAGES.hairPortrait} alt="Editorial hair portrait at Maison Neuf salon in Hell's Kitchen" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-7 md:pt-8">
          <Reveal>
            <p className="font-display text-2xl leading-snug md:text-4xl">
              Maison Neuf began with a simple frustration: nowhere in Midtown West did hair, nails, skin, and waxing at the same, uncompromising level.
            </p>
            <div className="mt-8 space-y-5 text-muted-foreground">
              <p>So in the heart of Hell's Kitchen, we built one house for all of it — an intentionally small team of specialists who obsess over their craft, in a space that feels less like a chain and more like a quiet, luxurious secret.</p>
              <p>Our mission is to give busy New Yorkers a place to exhale — where the standards are clinical, the artistry is genuine, and the experience is unmistakably yours. No rotating chairs, no rushed appointments, no compromise.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TEAM */}
      <section className="border-t border-border" data-testid="about-team">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
          <Reveal>
            <p className="label mb-6 text-wine">The specialists</p>
            <h2 className="max-w-2xl font-display text-4xl leading-[1.05] md:text-6xl">Meet the artists.</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <div className="group" data-testid={`team-member-${i}`}>
                  <div className="clip-frame aspect-[3/4] overflow-hidden bg-secondary">
                    <img src={m.img} alt={`${m.name}, ${m.role} at Maison Neuf`} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl">{m.name}</h3>
                  <p className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-wine">{m.role}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{m.specialty}</p>
                  <a href={BUSINESS.social.instagram} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <Instagram className="h-4 w-4" strokeWidth={1.5} /> {m.handle}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HYGIENE */}
      <section className="border-t border-border bg-secondary/30" data-testid="about-hygiene">
        <div className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
          <Reveal>
            <p className="label mb-6 text-wine">Cleanliness & sanitation</p>
            <h2 className="max-w-3xl font-display text-4xl leading-[1.05] md:text-6xl">Hygiene isn't a feature. It's the foundation.</h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-border md:grid-cols-3">
            {HYGIENE.map((h, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="h-full bg-background p-8 md:p-10">
                  <h.icon className="h-6 w-6 text-wine" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-2xl">{h.t}</h3>
                  <p className="mt-3 text-muted-foreground">{h.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-28" data-testid="about-community">
        <Reveal>
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="label mb-6 text-wine">In the neighborhood</p>
              <h2 className="font-display text-3xl leading-[1.05] md:text-5xl">Proudly Hell's Kitchen.</h2>
            </div>
            <div className="md:col-span-8 md:pt-4">
              <p className="text-muted-foreground md:text-lg">
                We hire locally, host seasonal community nights, and partner with nearby Theater District venues for pre-show glam. A portion of every membership supports NYC beauty-education scholarships — because the next generation of artists is part of this neighborhood too.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <CTABanner title="Come meet the team." sub="Book with a specialist, or call and we'll help you choose." />
    </>
  );
}
